import React from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import { IDoctor } from "../../../../dto/interfaces";
import { getGoogleMapsDirectionsUrl } from "../../../../utils/mapUtils";
import { Button } from "../../../../components/common/button";

// ContactItem sub-component (can live in this file or its own)
const ContactItem: React.FC<{
    icon: React.ElementType;
    label: string;
    children: React.ReactNode;
}> = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-blue-600">
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <span className="block text-sm font-semibold text-gray-500 uppercase">{label}</span>
            <p className="text-base font-medium text-gray-800">{children}</p>
        </div>
    </div>
);

interface ContactCardProps {
    doctor: IDoctor;
}

const ContactCard: React.FC<ContactCardProps> = ({ doctor }) => {
    const directionsUrl = doctor?.googleMapsLink || getGoogleMapsDirectionsUrl(doctor.address);

    const phoneNumber = doctor.publicPhone || "";
    const formattedPhone = `+${phoneNumber.slice(0, 2)}-${phoneNumber.slice(2)}`;

    // Build the display address (we don't need the util for this,
    // as we might want to format it differently for display)
    const displayAddress = `${doctor.address.addressLine1}, ${doctor.address.city}, ${doctor.address.state} ${doctor.address.pincode}`;

    return (
        <div className="bg-white rounded-[24px] shadow-sm md:shadow-xl p-6 md:p-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center md:text-left w-full">Contact us</h2>

            <div className="space-y-6">
                <ContactItem icon={MapPin} label="Location">
                    {displayAddress}
                </ContactItem>
                {phoneNumber && phoneNumber?.length > 0 && (
                    <ContactItem icon={Phone} label="Phone Number">
                        <a href={`tel:${phoneNumber}`} className="hover:text-blue-600">
                            {formattedPhone}
                        </a>
                    </ContactItem>
                )}
                <ContactItem icon={Mail} label="Email ID">
                    <a href={`mailto:${doctor.email}`} className="hover:text-blue-600 break-all">
                        {doctor.email}
                    </a>
                </ContactItem>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
                {directionsUrl && directionsUrl?.length > 0 && (
                    <Button
                        variant="primary" // Main action, use primary
                        onClick={() => window.open(directionsUrl, "_blank", "noopener,noreferrer")}
                        className="flex-1" // Allow it to fill space if needed
                    >
                        Get Directions
                    </Button>
                )}

                {/* Call Now Button */}
                {phoneNumber && phoneNumber.length > 0 && (
                    <Button
                        variant="secondary" // Secondary action
                        onClick={() => window.open(`tel:${phoneNumber}`, "_self")} // Use _self to initiate call on mobile
                        className="flex-1" // Allow it to fill space if needed
                    >
                        Call Now
                    </Button>
                )}
            </div>
        </div>
    );
};

export default ContactCard;
